import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Twitter, MessageCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useApp } from '@/context/AppContext';

const ContactPage = () => {
  const { settings, API } = useApp();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiry/contact`, formData);
      toast.success('Message sent! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header Banner */}
      <div className="bg-[#0c1b44] text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Contact Us</h1>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg">
            Get in touch with our expert team for any inquiries, wholesale orders, or support.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {/* Left Column: Contact Info & Map */}
          <div className="space-y-8">
            
            {/* Customer Care & Social Info */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Card className="hover:border-[#0c1b44] transition-colors shadow-sm">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-[#0c1b44]/10 flex items-center justify-center mb-4 mx-auto">
                    <Phone className="h-5 w-5 text-[#0c1b44]" />
                  </div>
                  <h3 className="font-bold text-navy mb-1 text-lg">Customer Care</h3>
                  <a href={`tel:${settings.company_phone || '+97142727815'}`} className="block text-[#0c1b44] hover:underline font-medium">
                    {settings.company_phone || '+971 4 272 7815'}
                  </a>
                  <p className="text-sm text-gray-500 mt-2">Mon - Sat, 9:00 AM - 6:00 PM</p>
                </CardContent>
              </Card>

              <Card className="hover:border-[#0c1b44] transition-colors shadow-sm">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 rounded-full bg-[#0c1b44]/10 flex items-center justify-center mb-4 mx-auto">
                    <Mail className="h-5 w-5 text-[#0c1b44]" />
                  </div>
                  <h3 className="font-bold text-navy mb-1 text-lg">Email Support</h3>
                  <a href={`mailto:${settings.company_email || 'sales@gpgt.ae'}`} className="block text-[#0c1b44] hover:underline font-medium break-all">
                    {settings.company_email || 'sales@gpgt.ae'}
                  </a>
                  <p className="text-sm text-gray-500 mt-2">We aim to reply within 24 hours.</p>
                </CardContent>
              </Card>
            </div>

            {/* Social Media Links */}
            <Card className="border-0 shadow-sm bg-gray-50">
              <CardContent className="p-6 text-center">
                <h3 className="font-bold text-navy mb-4">Connect With Us</h3>
                <div className="flex justify-center gap-4">
                  <a href={settings.facebook_url || 'https://www.facebook.com/profile.php?id=61587535891263'} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-blue-600 hover:bg-blue-600 hover:text-white transition-colors shadow-sm">
                    <Facebook className="h-5 w-5" />
                  </a>
                  <a href={settings.instagram_url || 'https://www.instagram.com/grandpalace.ae?igsh=MWdtaThhcTd6dG5oeA%3D%3D&utm_source=qr'} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-pink-600 hover:bg-pink-600 hover:text-white transition-colors shadow-sm">
                    <Instagram className="h-5 w-5" />
                  </a>
                  <a href={settings.linkedin_url || 'https://www.linkedin.com/in/grand-palace-general-trading-llc-2904063a8?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=ios_app'} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-blue-700 hover:bg-blue-700 hover:text-white transition-colors shadow-sm">
                    <Linkedin className="h-5 w-5" />
                  </a>
                  <a href={settings.twitter_url || 'https://x.com/GrandPalace2022'} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-black hover:bg-black hover:text-white transition-colors shadow-sm">
                    <Twitter className="h-5 w-5" />
                  </a>
                  <a href={`https://wa.me/${(settings.company_mobile || '971503992163').replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-green-500 hover:bg-green-500 hover:text-white transition-colors shadow-sm">
                    <MessageCircle className="h-5 w-5" />
                  </a>
                </div>
              </CardContent>
            </Card>

            {/* Google Map */}
            <Card className="overflow-hidden border-0 shadow-sm">
              <div className="h-80 w-full bg-gray-100 flex items-center justify-center relative">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3607.728522307559!2d55.3825177!3d25.2764152!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5d56e1c673b9%3A0xdb1f4d0ce5bc429d!2sGrand%20Palace%20General%20Trading%20L.L.C!5e0!3m2!1sen!2sae!4v1709477382949!5m2!1sen!2sae"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                  className="absolute inset-0 grayscale-[20%] hover:grayscale-0 transition-all duration-500"
                ></iframe>
              </div>
              <CardContent className="p-4 bg-white text-center border-t border-gray-100">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <MapPin className="h-5 w-5 text-[#0c1b44]" />
                  <span className="font-bold text-navy">Corporate Headquarters</span>
                </div>
                <p className="text-sm text-gray-600 max-w-md mx-auto">
                  {settings.company_address || 'Shop No:3 Al Sajaya Building, Al Qusais 2, Damascus St.  244, Sector, Dubai, United Arab Emirates'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Contact Form */}
          <div className="h-full">
            <Card className="shadow-lg border-[#0c1b44]/10 relative overflow-hidden h-full">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#0c1b44]" />
              <CardContent className="p-8 sm:p-10">
                <h2 className="text-2xl font-bold text-navy mb-2">Send us a Message</h2>
                <p className="text-gray-500 mb-8">Fill out the form below and our team will get back to you promptly.</p>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm font-semibold">Your Name</Label>
                      <Input id="name" required placeholder="John Doe" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="bg-gray-50 border-gray-200 focus:bg-white transition-colors h-11" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-sm font-semibold">Email Address</Label>
                      <Input id="email" type="email" required placeholder="john@example.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="bg-gray-50 border-gray-200 focus:bg-white transition-colors h-11" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-sm font-semibold">Phone Number</Label>
                      <Input id="phone" type="tel" placeholder="+971 50 123 4567" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="bg-gray-50 border-gray-200 focus:bg-white transition-colors h-11" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-sm font-semibold">Subject</Label>
                      <Input id="subject" required placeholder="How can we help?" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} className="bg-gray-50 border-gray-200 focus:bg-white transition-colors h-11" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-sm font-semibold">Message</Label>
                    <Textarea id="message" required placeholder="Write your message here..." className="min-h-[160px] resize-none bg-gray-50 border-gray-200 focus:bg-white transition-colors" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} />
                  </div>
                  
                  <Button type="submit" disabled={loading} className="w-full bg-[#0c1b44] hover:bg-[#1a2d64] text-white h-12 text-base font-semibold mt-4">
                    {loading ? 'Sending Message...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
